import {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  DetailedHTMLProps,
  Dispatch,
  FC,
  HTMLAttributes,
  ImgHTMLAttributes,
  InputHTMLAttributes,
  LabelHTMLAttributes,
  OptgroupHTMLAttributes,
  OptionHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  SetStateAction,
  TextareaHTMLAttributes,
} from 'react';
import { ISelectOption, MouseEventHandler, OptionMap } from './utils';

// helper for element types
export type ElementProps<
  T extends HTMLElement,
  A extends HTMLAttributes<T> = HTMLAttributes<T>
> = DetailedHTMLProps<A, T>;

// base element types
export type DivProps = ElementProps<HTMLDivElement>;
export type SpanProps = ElementProps<HTMLSpanElement>;
export type ParagraphProps = ElementProps<HTMLParagraphElement>;
export type UlProps = ElementProps<HTMLUListElement>;
export type LiProps = ElementProps<HTMLLIElement>;
export type AnchorProps = ElementProps<HTMLAnchorElement, AnchorHTMLAttributes<HTMLAnchorElement>>;
export type ImageProps = ElementProps<HTMLImageElement, ImgHTMLAttributes<HTMLImageElement>>;
export type LabelProps = ElementProps<HTMLLabelElement, LabelHTMLAttributes<HTMLLabelElement>>;
export type ButtonProps = ElementProps<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement>>;
export type InputProps = ElementProps<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>;
export type TextareaProps = ElementProps<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>;
export type SelectProps = ElementProps<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>;
export type OptionProps = ElementProps<HTMLOptionElement, OptionHTMLAttributes<HTMLOptionElement>>;
export type OptGroupProps = ElementProps<
  HTMLOptGroupElement,
  OptgroupHTMLAttributes<HTMLOptGroupElement>
>;

export interface ILabel extends LabelProps {
  required?: boolean;
}

export interface IField extends DivProps {
  label?: ReactNode;
  labelProps?: ILabel;
  fullwidth?: boolean;
  required?: boolean;
  errorMessage?: string;
}

// field element types
export type InputField = IField & InputProps;
export type SelectField = Omit<IField, 'onChange'> & ISelectComponent;

// form component types
export interface IInputComponent extends InputProps {
  controlProps?: DivProps;
  iconLeft?: JSX.Element;
  iconRight?: JSX.Element;
  fullwidth?: boolean;
}

export interface ISelectComponent extends SelectProps {
  options?: ISelectOption[];
  optionsMap?: OptionMap;
  controlProps?: DivProps;
  fullwidth?: boolean;
  usePlaceholderChild?: boolean;
}

export interface IAutocompleteComponent
  extends Omit<
    IInputComponent,
    'onChange' | 'onFocus' | 'onBlur' | 'onKeyDown' | 'onKeyUp' | 'onSelect' | 'defaultValue'
  > {
  defaultValue?: string;
  options: ISelectOption[];
  RenderOption?: FC<ISelectOption>;
  showOnFocus?: boolean;
  onSelect: (option: ISelectOption | null) => void;
  filterOn?: (filter: string) => (option: ISelectOption) => boolean;
}

export type AutocompleteField = IAutocompleteComponent &
  Omit<
    IField,
    'onChange' | 'onFocus' | 'onBlur' | 'onKeyDown' | 'onKeyUp' | 'onSelect' | 'defaultValue'
  >;

export type ButtonVariants =
  | 'default'
  | 'info'
  | 'warning'
  | 'danger'
  | 'success'
  | 'primary'
  | 'secondary'
  | 'primaryGradient'
  | 'secondaryGradient'
  | 'dark';

export interface IButton extends ButtonProps {
  size?: 'small' | 'normal' | 'medium' | 'large';
  fullwidth?: boolean;
  isLoading?: boolean;
  variant?: ButtonVariants;
  dot?: 'green' | 'white'; // may add other colors
}

export interface IUnbutton extends Omit<IButton, 'variant'> {
  variant?: 'default' | 'info' | 'warning' | 'danger' | 'success' | 'primary' | 'link';
}

export interface IDropdown extends DivProps {
  isRight?: boolean;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

export interface IDropdownTrigger extends IButton {
  id: string;
  handleToggle: MouseEventHandler;
  hideArrow?: boolean;
}

export interface IDropdownMenu extends DivProps {
  id: string;
}

export interface IDropdownItem extends DivProps {
  isHighlighted?: boolean;
}

export interface IDateComponent {
  selected: Date | null;
  disabled?: boolean;
  fullwidth?: boolean;
  onChange: (val: Date | null) => void;
  controlProps?: DivProps;
}

export type DateField = Omit<IField, 'onChange'> & IDateComponent;

export interface ISwitchComponent extends InputField {
  defaultChecked?: boolean;
}

export interface ICheckboxComponent extends InputField {
  defaultChecked?: boolean;
  errorMessage?: string;
}

export interface IRadioComponent extends InputField {
  defaultChecked?: boolean;
  errorMessage?: string;
}
